import { toPng } from 'html-to-image';
import { getLinearLightFontEmbedCSS } from './fonts';
import {
  getLinearLightFontEpoch,
  getLinearLightSourceSignature,
  reuseLinearLightWork,
} from './captureCache';

export const LINEAR_LIGHT = 'linear-light';
export class LinearLightLayoutPending extends Error {}
let nextSnapshotId = 0;
export const isLinearLightBlendMode = (mode: unknown) => mode === LINEAR_LIGHT;

// Only the canvas's own solid background can guarantee an opaque backdrop.
export function hasOpaqueLinearLightBackground(
  pattern?: { type?: string; color?: { a?: number } } | null
) {
  return Boolean(
    pattern?.color &&
      (pattern.type === undefined || pattern.type === 'solid') &&
      (pattern.color.a === undefined || pattern.color.a === 1)
  );
}

// Source-over alpha is applied AFTER the Linear Light RGB result is clipped.
export function compositeLinearLightPixels(
  backdrop: Uint8ClampedArray,
  source: Uint8ClampedArray,
  opacity: number
) {
  if (backdrop.length !== source.length || backdrop.length % 4 !== 0) {
    throw new Error('Linear Light requires matching RGBA buffers.');
  }
  const coverage = Math.min(1, Math.max(0, opacity));
  for (let i = 0; i < backdrop.length; i += 4) {
    const as = (source[i + 3] / 255) * coverage;
    const ab = backdrop[i + 3] / 255;
    const ao = as + ab * (1 - as);
    for (let c = 0; c < 3; c++) {
      const cb = backdrop[i + c];
      const cs = source[i + c];
      const blend = Math.min(255, Math.max(0, cb + 2 * cs - 255));
      backdrop[i + c] = ao
        ? (as * (1 - ab) * cs + as * ab * blend + (1 - as) * ab * cb) / ao
        : 0;
    }
    backdrop[i + 3] = ao * 255;
  }
}

export function loadLinearLightImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Unable to load Linear Light source.'));
    image.src = src;
  });
}

// Copy computed presentation synchronously while the editor's concealment is
// disabled. Restore it before the browser paints; keep the one native editor.
export function copyLinearLightPresentation(
  original: Element,
  include: (element: Element) => boolean = () => true
): Element {
  const clone = original.cloneNode(false) as Element;
  if (original instanceof HTMLElement || original instanceof SVGElement) {
    const style = getComputedStyle(original);
    const target = clone as HTMLElement | SVGElement;
    for (const name of Array.from(style))
      target.style.setProperty(
        name,
        style.getPropertyValue(name),
        style.getPropertyPriority(name)
      );
  }
  if (original instanceof HTMLVideoElement) {
    if (original.error) throw new Error('Unable to sample backdrop video.');
    if (original.readyState < 2)
      throw new LinearLightLayoutPending('Backdrop video is still loading.');
    const canvas = document.createElement('canvas');
    canvas.width = original.videoWidth;
    canvas.height = original.videoHeight;
    const context = canvas.getContext('2d');
    if (!context)
      throw new Error('Backdrop capture requires a canvas context.');
    context.drawImage(original, 0, 0);
    const image = document.createElement('img');
    image.src = canvas.toDataURL();
    image.style.cssText = (clone as HTMLElement).style.cssText;
    return image;
  }
  if (original instanceof HTMLCanvasElement) {
    const image = document.createElement('img');
    image.src = original.toDataURL();
    image.style.cssText = (clone as HTMLCanvasElement).style.cssText;
    return image;
  }
  if (clone instanceof HTMLElement || clone instanceof SVGElement) {
    clone.style.animation = 'none';
    clone.style.transition = 'none';
  }
  for (const node of Array.from(original.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE && !include(node as Element))
      continue;
    clone.appendChild(
      node.nodeType === Node.ELEMENT_NODE
        ? copyLinearLightPresentation(node as Element, include)
        : node.cloneNode(true)
    );
  }
  return clone;
}

export type LinearLightCaptureBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

// Include every nontransparent pixel, including antialiased edges and shadows.
export function getPaintedPixelBounds(
  data: Uint8ClampedArray,
  width: number,
  height: number
) {
  let left = width,
    top = height,
    right = -1,
    bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!data[(y * width + x) * 4 + 3]) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
  }
  if (right < left) return { x: 0, y: 0, width: 1, height: 1 };
  left = Math.max(0, left - 1);
  top = Math.max(0, top - 1);
  right = Math.min(width - 1, right + 1);
  bottom = Math.min(height - 1, bottom + 1);
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

export async function cropLinearLightSource(
  uri: string,
  bounds: LinearLightCaptureBounds
) {
  const image = await loadLinearLightImage(uri);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Unable to inspect Linear Light source.');
  context.drawImage(image, 0, 0);
  const paint = getPaintedPixelBounds(
    context.getImageData(0, 0, canvas.width, canvas.height).data,
    canvas.width,
    canvas.height
  );
  if (
    paint.x === 0 &&
    paint.y === 0 &&
    paint.width === canvas.width &&
    paint.height === canvas.height
  ) {
    return { uri, bounds };
  }
  const scaleX = bounds.width / canvas.width,
    scaleY = bounds.height / canvas.height;
  // Copy before resizing: resizing a canvas clears its pixels.
  const pixels = context.getImageData(
    paint.x,
    paint.y,
    paint.width,
    paint.height
  );
  canvas.width = paint.width;
  canvas.height = paint.height;
  context.putImageData(pixels, 0, 0);
  const cropped = canvas.toDataURL();
  await loadLinearLightImage(cropped);
  return {
    uri: cropped,
    bounds: {
      x: bounds.x + paint.x * scaleX,
      y: bounds.y + paint.y * scaleY,
      width: paint.width * scaleX,
      height: paint.height * scaleY,
    },
  };
}

// Retain off-page pixels so an existing texture can move back across an edge.
export function getLinearLightCaptureBounds(
  source: HTMLElement,
  paintOnly = false
) {
  const nodes = paintOnly
    ? Array.from(source.querySelectorAll('*')).filter(
        (node) => !node.closest('defs')
      )
    : [];
  // Preserve the full capture for paint whose overhang is not a DOM box.
  if (
    paintOnly &&
    nodes.some((node) => {
      // HTML layout boxes omit italic ink and zero-height glyph markers.
      if (
        node instanceof HTMLElement &&
        Array.from(node.childNodes).some(
          (child) =>
            child.nodeType === Node.TEXT_NODE && child.textContent?.trim()
        )
      )
        return true;
      const style = getComputedStyle(node);
      return (
        (style.boxShadow && style.boxShadow !== 'none') ||
        (style.textShadow && style.textShadow !== 'none') ||
        (style.filter && style.filter !== 'none') ||
        (style.stroke && style.stroke !== 'none') ||
        (node.getAttribute('filter') &&
          node.getAttribute('filter') !== 'none') ||
        (node.getAttribute('stroke') && node.getAttribute('stroke') !== 'none')
      );
    })
  )
    return getLinearLightCaptureBounds(source);
  const root = source.getBoundingClientRect();
  const scaleX = root.width ? source.offsetWidth / root.width : 1;
  const scaleY = root.height ? source.offsetHeight / root.height : 1;
  let left = paintOnly ? Infinity : 0;
  let top = paintOnly ? Infinity : 0;
  let right = paintOnly ? -Infinity : source.offsetWidth;
  let bottom = paintOnly ? -Infinity : source.offsetHeight;
  const children = Array.from(source.children).flatMap((child) =>
    child.hasAttribute('data-linear-light-coordinates')
      ? [child, ...Array.from(child.children)]
      : [child]
  );
  for (const child of paintOnly ? nodes : children) {
    if (!(child instanceof HTMLElement) && !(child instanceof SVGElement))
      continue;
    const bounds = child.getBoundingClientRect();
    if (paintOnly && (!bounds.width || !bounds.height)) continue;
    left = Math.min(left, (bounds.left - root.left) * scaleX);
    top = Math.min(top, (bounds.top - root.top) * scaleY);
    right = Math.max(right, (bounds.right - root.left) * scaleX);
    bottom = Math.max(bottom, (bounds.bottom - root.top) * scaleY);
  }
  if (!Number.isFinite(left)) return getLinearLightCaptureBounds(source);
  // One CSS pixel includes fractional antialias coverage at any display density.
  const margin = paintOnly ? 1 : 0;
  const x = Math.floor(left - margin);
  const y = Math.floor(top - margin);
  const captureBounds = {
    x,
    y,
    width: Math.ceil(right + margin) - x,
    height: Math.ceil(bottom + margin) - y,
  };
  if (paintOnly) {
    const fullBounds = getLinearLightCaptureBounds(source);
    // Clipped media can report a huge descendant box outside its visible frame.
    // Keep the bounded optimization only when neither raster dimension grows.
    if (
      captureBounds.width > fullBounds.width ||
      captureBounds.height > fullBounds.height
    )
      return fullBounds;
  }
  return captureBounds;
}

type CaptureOptions = {
  skipFonts?: boolean;
  backdropMask?: boolean;
  paintMask?: boolean;
};

export async function captureLinearLightSource(
  source: HTMLElement,
  bounds?:
    | LinearLightCaptureBounds
    | (() => LinearLightCaptureBounds | undefined),
  options: CaptureOptions = {}
): Promise<string> {
  if (!options.skipFonts) await document.fonts.ready;
  await Promise.all(
    Array.from(source.querySelectorAll('img')).map(async (image) => {
      try {
        await image.decode();
      } catch {
        throw new Error('Unable to decode Linear Light source image.');
      }
    })
  );
  if (source.querySelector('[data-lut-status="loading"]')) {
    throw new Error('Linear Light source is still processing.');
  }
  // Measure alongside the synchronous copy, after image/font readiness.
  const captureBounds = typeof bounds === 'function' ? bounds() : bounds;
  // Equal markup at this settled moment renders equal pixels. The copy below
  // is synchronous, so the signature describes exactly what gets rendered.
  return reuseLinearLightWork(
    getLinearLightSourceSignature(source, {
      ...options,
      bounds: captureBounds,
      fonts: options.skipFonts ? 0 : getLinearLightFontEpoch(),
    }),
    () => renderLinearLightSnapshot(source, captureBounds, options)
  );
}

async function renderLinearLightSnapshot(
  source: HTMLElement,
  captureBounds: LinearLightCaptureBounds | undefined,
  options: CaptureOptions
): Promise<string> {
  const native = source.getAttribute('data-linear-light-native');
  source.removeAttribute('data-linear-light-native');
  let snapshot: HTMLElement;
  try {
    snapshot = copyLinearLightPresentation(
      source,
      (node) => !node.hasAttribute('data-combined-crop-guide')
    ) as HTMLElement;
  } finally {
    if (native !== null)
      source.setAttribute('data-linear-light-native', native);
  }
  snapshot.removeAttribute('data-linear-light-native');
  const ids = new Map<string, string>();
  const prefix = `ll-source-${++nextSnapshotId}-`;
  for (const element of Array.from(snapshot.querySelectorAll('[id]'))) {
    const original = element.id;
    element.id = prefix + original;
    ids.set(original, element.id);
  }
  for (const element of Array.from(snapshot.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      const rewritten = attribute.value.replace(
        /url\(["']?[^)"']*#([^)"']+)["']?\)/g,
        (match, id) => (ids.has(id) ? `url("#${ids.get(id)}")` : match)
      );
      element.setAttribute(
        attribute.name,
        (attribute.name === 'href' || attribute.name === 'xlink:href') &&
          ids.has(rewritten.slice(1)) &&
          rewritten.startsWith('#')
          ? `#${ids.get(rewritten.slice(1))}`
          : rewritten
      );
    }
  }
  // The loading-state soft frame is presentation of the fallback, not paint.
  for (const frame of Array.from(
    snapshot.querySelectorAll<HTMLElement>('[data-combined-soft-frame]')
  )) {
    frame.style.setProperty('mask-image', 'none');
    frame.style.setProperty('-webkit-mask-image', 'none');
  }
  // The effect footprint is independent of image/fill alpha. Capture the
  // existing frame mask separately; foreground captures never sample a page.
  const markers = Array.from(
    snapshot.querySelectorAll<HTMLElement>('.element-backdrop')
  );
  if (options.backdropMask || options.paintMask) {
    for (const node of Array.from(
      snapshot.querySelectorAll<HTMLElement>('*')
    )) {
      node.style.visibility = 'hidden';
    }
    for (const marker of options.paintMask
      ? Array.from(
          snapshot.querySelectorAll<HTMLElement>('[data-combined-paint-frame]')
        )
      : markers) {
      // Capture geometry before feathering. The compositor derives both
      // paint normalization and final coverage from this same frame.
      const frameMask = marker.style.getPropertyValue('--backdrop-frame-mask');
      if (frameMask) {
        marker.style.setProperty('mask-image', frameMask);
        marker.style.setProperty('-webkit-mask-image', frameMask);
      }
      marker.style.visibility = 'visible';
      marker.style.background = 'white';
      marker.style.borderColor = 'white';
      marker.style.opacity = '1';
      marker.style.setProperty('backdrop-filter', 'none');
      marker.style.setProperty('-webkit-backdrop-filter', 'none');
    }
  } else {
    markers.forEach((marker) => marker.remove());
  }
  snapshot.style.opacity = '1';
  snapshot.style.pointerEvents = 'none';
  const width = source.offsetWidth;
  const height = source.offsetHeight;
  if (!width || !height)
    throw new LinearLightLayoutPending(
      'Linear Light source has no dimensions.'
    );
  snapshot.style.width = `${width}px`;
  snapshot.style.height = `${height}px`;
  snapshot.style.inset = 'auto';
  snapshot.style.top = '0';
  snapshot.style.left = '0';
  const buffer = document.createElement('div');
  Object.assign(buffer.style, {
    position: 'fixed',
    left: '-100000px',
    top: '0',
    width: `${width}px`,
    height: `${height}px`,
    opacity: '0',
    pointerEvents: 'none',
  });
  buffer.append(snapshot);
  document.body.append(buffer);
  try {
    const uri = await toPng(snapshot, {
      width: captureBounds?.width ?? width,
      height: captureBounds?.height ?? height,
      pixelRatio: window.devicePixelRatio || 1,
      skipFonts: options.skipFonts,
      fontEmbedCSS: options.skipFonts
        ? ''
        : await getLinearLightFontEmbedCSS(snapshot),
      backgroundColor: 'transparent',
      style: {
        opacity: '1',
        ...(captureBounds
          ? {
              width: `${width}px`,
              height: `${height}px`,
              overflow: 'visible',
              transform: `translate(${-captureBounds.x}px, ${-captureBounds.y}px)`,
            }
          : {}),
      },
    });
    await loadLinearLightImage(uri);
    return uri;
  } finally {
    buffer.remove();
  }
}

export async function waitForLinearLight(node: HTMLElement, timeoutMs = 10000) {
  const pending = () =>
    node.querySelector('[data-linear-light-status="loading"]');
  const failed = () => node.querySelector('[data-linear-light-status="error"]');
  if (!pending()) {
    if (failed()) throw new Error('Unable to render Linear Light.');
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const observer = new MutationObserver(check);
    const timeout = setTimeout(() => {
      observer.disconnect();
      reject(new Error('Timed out rendering Linear Light.'));
    }, timeoutMs);
    function check() {
      if (pending()) return;
      clearTimeout(timeout);
      observer.disconnect();
      if (failed()) reject(new Error('Unable to render Linear Light.'));
      else resolve();
    }
    observer.observe(node, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-linear-light-status'],
    });
    check();
  });
}
